import React, { useRef } from "react";
import { Link, useLocation } from "wouter";
import ArticleRoundedIcon from "@mui/icons-material/ArticleRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DarkModeRoundedIcon from "@mui/icons-material/DarkModeRounded";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import LightModeRoundedIcon from "@mui/icons-material/LightModeRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import PlaylistAddCheckRoundedIcon from "@mui/icons-material/PlaylistAddCheckRounded";
import ShowChartRoundedIcon from "@mui/icons-material/ShowChartRounded";

import { useEscapeKey } from "../../hooks/useEscapeKey";
import { useModalA11y } from "../../hooks/useModalA11y";
import { useTheme } from "../../hooks/useTheme";
import { IconButton } from "../IconButton";
import { VisuallyHidden } from "../VisuallyHidden";
import {
  ACTIVITY_PATH,
  ADMIN_PATH,
  INSTRUMENTS_PATH,
  JOBS_PATH,
  SCHEDULERS_PATH,
  usePendingWorkCount,
  WORK_QUEUE_PATH,
} from "../../pages/Admin";
import { GRAFANA_URL, LOKI_URL } from "../../pages/Admin/constants";
import { plural } from "../../pages/Admin/utils/format";
import {
  BrandCaption,
  BrandCopy,
  BrandMark,
  DrawerClose,
  Sidebar,
  SidebarAction,
  SidebarBadge,
  SidebarBrand,
  SidebarDivider,
  SidebarFooter,
  SidebarGroup,
  SidebarLink,
} from "./styles";
import { AdminNavItem, AdminSidebarProps } from "./types";

const NAV_ITEMS: AdminNavItem[] = [
  {
    href: ADMIN_PATH,
    label: "Overview",
    icon: <DashboardRoundedIcon fontSize="small" />,
    drilldownPaths: [SCHEDULERS_PATH],
  },
  {
    href: WORK_QUEUE_PATH,
    label: "Work queue",
    icon: <PlaylistAddCheckRoundedIcon fontSize="small" />,
    drilldownPaths: [JOBS_PATH],
    showsPendingWork: true,
  },
  {
    href: INSTRUMENTS_PATH,
    label: "Instruments",
    icon: <ShowChartRoundedIcon fontSize="small" />,
    drilldownPaths: [],
  },
  {
    href: ACTIVITY_PATH,
    label: "Activity",
    icon: <HistoryRoundedIcon fontSize="small" />,
    drilldownPaths: [],
  },
];

const currentOf = (
  { href, drilldownPaths }: AdminNavItem,
  location: string,
) => {
  if (location === href) {
    return "page";
  }
  return drilldownPaths.includes(location) ? "location" : undefined;
};

export const AdminSidebar: React.FC<AdminSidebarProps> = ({
  id,
  isDrawerOpen,
  isLoggingOut,
  onCloseDrawer,
  onLogout,
}) => {
  const sidebarRef = useRef<HTMLElement>(null);
  const [location] = useLocation();
  const { onToggleTheme, isDarkTheme } = useTheme();
  const pendingWorkCount = usePendingWorkCount();

  useModalA11y(isDrawerOpen, sidebarRef);
  useEscapeKey(onCloseDrawer, isDrawerOpen);

  const themeLabel = isDarkTheme ? "Dark theme" : "Light theme";
  const nextThemeLabel = isDarkTheme ? "light theme" : "dark theme";
  const themeIcon = isDarkTheme ? (
    <LightModeRoundedIcon fontSize="small" />
  ) : (
    <DarkModeRoundedIcon fontSize="small" />
  );
  const grafanaLink = GRAFANA_URL ? (
    <SidebarLink href={GRAFANA_URL} target="_blank" rel="noreferrer">
      <InsightsRoundedIcon fontSize="small" />
      Grafana
    </SidebarLink>
  ) : null;
  const lokiLink = LOKI_URL ? (
    <SidebarLink href={LOKI_URL} target="_blank" rel="noreferrer">
      <ArticleRoundedIcon fontSize="small" />
      Loki logs
    </SidebarLink>
  ) : null;
  const navLinks = NAV_ITEMS.map((item) => {
    const current = currentOf(item, location);
    const hasBadge = item.showsPendingWork && pendingWorkCount > 0;
    return (
      <Link key={item.href} href={item.href} onClick={onCloseDrawer} asChild>
        <SidebarLink $isActive={current !== undefined} aria-current={current}>
          {item.icon}
          {item.label}
          {hasBadge && (
            <SidebarBadge>
              <span aria-hidden="true">{pendingWorkCount}</span>
              <VisuallyHidden>
                {plural(pendingWorkCount, "pending item")}
              </VisuallyHidden>
            </SidebarBadge>
          )}
        </SidebarLink>
      </Link>
    );
  });

  return (
    <Sidebar
      ref={sidebarRef}
      id={id}
      $isOpen={isDrawerOpen}
      tabIndex={-1}
      role={isDrawerOpen ? "dialog" : undefined}
      aria-modal={isDrawerOpen ? true : undefined}
      aria-label="Admin sidebar"
    >
      <DrawerClose>
        <IconButton aria-label="Close navigation" onClick={onCloseDrawer}>
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      </DrawerClose>
      <Link href={ADMIN_PATH} onClick={onCloseDrawer} asChild>
        <SidebarBrand>
          <BrandMark aria-hidden="true">N</BrandMark>
          <BrandCopy>
            Net Worth Tracker
            <BrandCaption>Admin</BrandCaption>
          </BrandCopy>
        </SidebarBrand>
      </Link>
      <SidebarGroup aria-label="Admin navigation">{navLinks}</SidebarGroup>
      <SidebarFooter>
        {grafanaLink}
        {lokiLink}
        <SidebarDivider />
        <SidebarAction
          type="button"
          onClick={onToggleTheme}
          aria-label={`${themeLabel}: switch to ${nextThemeLabel}`}
        >
          {themeIcon}
          {themeLabel}
        </SidebarAction>
        <SidebarAction
          type="button"
          onClick={onLogout}
          disabled={isLoggingOut}
          aria-busy={isLoggingOut || undefined}
        >
          <LogoutRoundedIcon fontSize="small" />
          {isLoggingOut ? "Logging out..." : "Log out"}
        </SidebarAction>
      </SidebarFooter>
    </Sidebar>
  );
};
